import React from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/Label'
import { Key, CheckCircle, XCircle } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Field from '@/components/ui/Field'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import useUpdatePassword from '@/services/api/auth/useUpdatePassword'
import { PasswordInput } from '@/components/PasswordInput'

export default function PasswordSection() {
  const passwordRequirements = [
    { label: 'At least 8 characters', test: (pwd: string) => pwd.length >= 8 },
    {
      label: 'Contains uppercase letter',
      test: (pwd: string) => /[A-Z]/.test(pwd),
    },
    {
      label: 'Contains lowercase letter',
      test: (pwd: string) => /[a-z]/.test(pwd),
    },
    { label: 'Contains number', test: (pwd: string) => /\d/.test(pwd) },
    {
      label: 'Contains special character',
      test: (pwd: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    },
  ]

  const isPasswordValid = (password: string) => {
    return passwordRequirements.every((req) => req.test(password))
  }

  const schema = z
    .object({
      currentPassword: z.string().min(1, 'Current password is required'),
      newPassword: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .refine(
          isPasswordValid,
          'Password does not meet security requirements',
        ),
      confirmPassword: z.string().min(1, 'Please confirm your password'),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    })
    .refine((data) => data.currentPassword !== data.newPassword, {
      message: 'New password must be different from current password',
      path: ['newPassword'],
    })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const { setError, formState, handleSubmit, reset, watch } = form
  const { errors } = formState
  const newPassword = watch('newPassword')

  const { action: updatePassword, status: updatePasswordStatus } =
    useUpdatePassword({
      setError,
      clientServerFieldMapping: {
        currentPassword: 'current_password',
        newPassword: 'password',
      },
    })

  const handlePasswordChange = async (values: any) => {
    await updatePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    })
    reset()
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2">Change Password</h2>
        <p className="mb-4 text-gray-600">
          Update your password to keep your account secure.
        </p>
      </div>

      <form onSubmit={handleSubmit(handlePasswordChange)} className="space-y-6">
        <Field
          statusText={errors?.currentPassword?.message?.toString()}
          status={errors?.currentPassword ? 'error' : null}
        >
          <Label htmlFor="currentPassword">Current Password</Label>
          <PasswordInput
            id="currentPassword"
            {...form.register('currentPassword')}
            placeholder="Enter your current password"
          />
          <FieldStatusMessage />
        </Field>

        <Field
          statusText={errors?.newPassword?.message?.toString()}
          status={errors?.newPassword ? 'error' : null}
        >
          <Label htmlFor="newPassword">New Password</Label>
          <PasswordInput
            id="newPassword"
            {...form.register('newPassword')}
            placeholder="Enter your new password"
          />
          <FieldStatusMessage />
        </Field>

        <Field
          statusText={errors?.confirmPassword?.message?.toString()}
          status={errors?.confirmPassword ? 'error' : null}
        >
          <Label htmlFor="confirmPassword">Confirm New Password</Label>
          <PasswordInput
            id="confirmPassword"
            {...form.register('confirmPassword')}
            placeholder="Confirm your new password"
          />
          <FieldStatusMessage />
        </Field>

        {newPassword && (
          <div className="rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 flex items-center gap-2">
              <Key className="h-4 w-4" />
              Password Requirements
            </h4>
            <ul className="space-y-1">
              {passwordRequirements.map((req, index) => {
                const isValid = req.test(newPassword)
                return (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    {isValid ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )}
                    <span
                      className={isValid ? 'text-green-700' : 'text-gray-600'}
                    >
                      {req.label}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        <Button
          type="submit"
          disabled={updatePasswordStatus.status === 'pending'}
        >
          {updatePasswordStatus.status === 'pending'
            ? 'Updating Password...'
            : 'Update Password'}
        </Button>
      </form>
    </div>
  )
}
